import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import swtAlert from "lib/sweetAlert";
import {
  buildInvestReturnLocation,
  buildStripeCheckoutReturnUrls,
  clearPendingCheckout,
  createInvestmentCheckout,
  readPendingCheckout,
  redirectToStripeCheckout,
  savePendingCheckout,
  syncInvestmentSession,
} from "lib/investmentHelper";
import { selectIsAuthenticated } from "store/reducer";

function isPaidCheckoutStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "paid" || s === "completed" || s === "succeeded";
}

async function tryCompletePendingCheckout(pending, projectId, amount, navigate) {
  if (!pending?.sessionId) return false;
  if (pending.projectId !== projectId || Number(pending.amount) !== Number(amount)) {
    return false;
  }

  try {
    const synced = await syncInvestmentSession(pending.sessionId);
    if (!isPaidCheckoutStatus(synced.status)) return false;

    swtAlert.close();
    clearPendingCheckout();
    await swtAlert.success(
      "Payment confirmed",
      "Your investment has been recorded successfully."
    );
    navigate("/dashboard", { replace: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * Shared investment checkout flow: validation, duplicate guard, API checkout,
 * Stripe redirect, and optional resume of a pending session.
 */
export function useInvestmentCheckout({
  projectId,
  amount,
  minInvestment = 0,
  maxInvestment,
  campaignStartAt,
  campaignEndAt,
  daysRemaining,
  status,
} = {}) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const submitLockRef = useRef(false);

  const startCheckout = useCallback(async () => {
    if (!isAuthenticated) {
      swtAlert.warning(
        "Sign in required",
        "Please sign in to invest in this campaign."
      );
      const returnTo =
        location.pathname === `/invest/${projectId}`
          ? location
          : buildInvestReturnLocation(
              projectId,
              amount,
              minInvestment,
              maxInvestment
            );
      navigate("/sign-in", { state: { from: returnTo } });
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setLoading(true);
    swtAlert.loader("Preparing checkout", "Connecting to secure payment…");

    let navigatingToStripe = false;
    try {
      const pending = readPendingCheckout();
      const resumed = await tryCompletePendingCheckout(
        pending,
        projectId,
        amount,
        navigate
      );
      if (resumed) return;

      const returnUrls = buildStripeCheckoutReturnUrls();
      const checkout = await createInvestmentCheckout(projectId, amount, {
        returnUrls,
      });

      if (!checkout.sessionId && !checkout.checkoutUrl) {
        throw new Error(
          "Checkout session was not returned. Please try again or contact support."
        );
      }

      savePendingCheckout({
        projectId,
        amount: Number(amount),
        sessionId: checkout.sessionId,
        investmentId: checkout.investmentId,
        returnUrls,
      });

      swtAlert.close();
      const didRedirect = await Promise.resolve(
        redirectToStripeCheckout({
          sessionId: checkout.sessionId,
          checkoutUrl: checkout.checkoutUrl,
        })
      );
      if (didRedirect) {
        navigatingToStripe = true;
        return;
      }
    } catch (err) {
      swtAlert.close();
      const message =
        err?.message ||
        "We could not start checkout. Check your connection and try again.";
      swtAlert.error("Checkout failed", message);
    } finally {
      if (!navigatingToStripe) {
        setLoading(false);
        submitLockRef.current = false;
      }
    }
  }, [
    projectId,
    amount,
    minInvestment,
    maxInvestment,
    campaignStartAt,
    campaignEndAt,
    daysRemaining,
    status,
    isAuthenticated,
    navigate,
    location,
  ]);

  return { startCheckout, loading, isAuthenticated };
}
